import axios from 'axios'
import { API } from 'constants/apiEndPoints'
import { MSG_TYPE } from 'constants/index'
import { useReactGA4 } from 'GA4/useReactGA4'
import { useUser } from 'hooks/user/useUser'
import { isObject, uniqueId } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useIntervalWhen } from 'utils/hooks/useIntervalWhen'
import { parseAchievement } from './messagesParser/achievementParser'
import { parseUserMessage } from './messagesParser/userParser'
import { parseAnnouncement } from './messagesParser/announcementParser'
import { isAddressesEqual } from 'utils/wallet/addresses'
import { emitCustomEvent } from 'react-custom-events'
import { EVENTS } from 'constants/events'
import { useSocketListener } from 'hooks/sockets/useSocket'

const INIT_MSG_COUNT = 150

const sendMessages = async (
  {
    payload,
    accessToken,
  }: {
    payload: TSendPayload
    accessToken: string
  },
  options = {},
) => {
  try {
    const res = await axios.post(`${API.CHAT_SEND_MESSAGE}`, payload, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    // return res.data.map(parseUserMessage)
  } catch (err) {
    console.error('message err:: ', err)
  }
}

const fetchMessages = async (myAddress: string, count: Number, options = {}, accessToken: string = '') => {
  try {
    let res
    if (accessToken) {
      res = await axios.get(`${API.CHAT_INIT}?count=${count}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    } else res = await axios.get(`${API.CHAT_MESSAGES}?count=${count}`, options)

    //Make it compatible with multiple types
    const parsedMessages = res.data.map((rawMessage: TRawUserMessage | TRawAnnouncement | TRawAchievement) => {
      const messageType = rawMessage.messageType

      switch (messageType) {
        case MSG_TYPE.USER:
          return parseUserMessage(rawMessage as TRawUserMessage, true)
        case MSG_TYPE.ANNOUNCEMENT:
          return parseAnnouncement(rawMessage as TRawAnnouncement, true)
        case MSG_TYPE.ACHIEVEMENT:
          if (!isAddressesEqual(rawMessage.address, myAddress)) return null
          return parseAchievement(rawMessage as TRawAchievement, true)
        default:
          return parseUserMessage(rawMessage as TRawUserMessage, true)
      }
    })
    const filteredMessages = parsedMessages.filter((item: any) => !!item)
    return filteredMessages
    // return res.data.map(parseUserMessage)
  } catch (err) {
    console.error('message err:: ', err)
  }
}

const mergeMessages = (prev: TParsedMessage[], incoming: TParsedMessage[]) => {
  const compareMessages = (a: TParsedMessage, b: TParsedMessage) => +new Date(a.timestamp) - +new Date(b.timestamp)
  const isDuplicate = (a: TParsedMessage, b: TParsedMessage) => {
    if (a.messageId && b.messageId) return a.messageId === b.messageId

    if (a.timestamp !== b.timestamp) return false

    if (
      'text' in a.message &&
      'text' in b.message &&
      a.address === b.address &&
      (a.message as MessageBody).text === (b.message as MessageBody).text
    )
      return true
  }

  if (!prev.length) return incoming.sort(compareMessages)
  if (!incoming.length) return prev.sort(compareMessages)

  return [...prev.filter((a) => !incoming.some((b) => isDuplicate(a, b))), ...incoming].sort(compareMessages)
}

export const useChatMessagesManager = () => {
  const { account, accessToken } = useUser('wallet')
  const { username, avatar } = useUser('profile')
  const { gaEvent } = useReactGA4()

  const [messages, setMsgs] = useState<TParsedMessage[]>([])
  const updateMessages = useCallback(
    (incoming: TParsedMessage[] = []) => {
      setMsgs((prev) => mergeMessages(prev, incoming))
    },
    [setMsgs],
  )

  //Initial data fetch
  useEffect(() => {
    // if (!messages.length) {
    const controller = new AbortController()
    fetchMessages(account, INIT_MSG_COUNT, { signal: controller.signal }, accessToken)
      .then(updateMessages)
      .catch((e) => console.warn(e))
    return () => {
      controller.abort()
    }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  // Handlers
  const handleMessage = useCallback(
    (msg: TRawUserMessage): void => {
      updateMessages([parseUserMessage(msg)])
    },
    [updateMessages],
  )

  const handleAnnoucement = useCallback((msg: TRawAnnouncement): void | undefined => {
    if (msg.messageType !== MSG_TYPE.ANNOUNCEMENT) return
    if (!msg.messageId) return
    updateMessages([parseAnnouncement(msg)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAchievement = useCallback((msg: TRawAchievement): void | undefined => {
    if (msg.messageType !== MSG_TYPE.ACHIEVEMENT) return
    emitCustomEvent(EVENTS.USER_ACHIEVEMENT_NEW)
    updateMessages([parseAchievement(msg)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Listener
  useSocketListener('gateway', 'chat.public', handleMessage)
  useSocketListener('gateway','announcement.public', handleAnnoucement)
  useSocketListener('gateway','achievement.private', handleAchievement)

  // Just for testing
  useIntervalWhen(
    () => {
      const msg = {
        messageId: uniqueId(),
        timestamp: 'ahora',
        messageType: 'achievement',
        address: 'fhfhf',
        message: JSON.stringify([
          {
            achievementName: '💪 Good Work If You Can Get It',
            achievementXp: 50,
          },
          {
            achievementName: '🥇 Winner Winner',
            achievementXp: 25,
          },
          {
            achievementName: '🦑 Giant Squid',
            achievementXp: 25,
          },
          {
            achievementName: '👨🏻‍⚕️ Friend of Ours',
            achievementXp: 25,
          },
        ]),
      }
      handleAchievement(msg)
    },
    5000,
    false,
    true,
  )
  // Just for testing

  // Sending User Message - DISABLED PERMANENTLY
  const sendUserMessage = (content: MessageBody) => {
    // DISABLED: Chat message sending permanently disabled
    console.log('Chat message sending is permanently disabled')
    return
    
    /* DISABLED CODE:
    if (!isObject(content) || account === '') return
    try {
      const message = JSON.stringify(content)
      const payload = {
        address: account,
        message,
        timestamp: new Date().toISOString(),
        messageType: MSG_TYPE.USER,
        avatar,
      }
      const controller = new AbortController()
      sendMessages({ payload, accessToken }, { signal: controller.signal })
      gaEvent('chat_send', { pathId: 'chat.send', messageContent: content })
      updateMessages([
        {
          ...payload,
          message: content,
          username,
          avatar,
        } as TParsedMessage,
      ])
    } catch (err) {
      console.log('ACZ stringify error::', err)
      return
    }
    */
  }

  return { messages, sendUserMessage }
}
